import { IBaseChain, IChainMapping } from 'FeatureModules/ComponentChain/types/chain.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { ImageType, ImageVariant, TMediaType } from 'FeatureModules/Media/types/media.types';
import { ELanguage } from 'DesignSystem/types/language.types';

export interface IBaseProduct {
  id: string;
  imageUrl: string;
  image: ImageVariant | null;
  slug: string;
  title: string;
  languageConfig: ILanguageConfig;
  articleNumber?: string;
  modifiedTimestamp?: Date;
  supplyChainId?: string;
  latestSupplyChain?: Pick<IBaseChain, 'id' | 'chainTasksDetails'>;
  connectedPartnerCompanies?: IBasePartnerCompany[];
}

export enum EContentBlockType {
  ABOUT = 'About',
  GENERAL = 'General',
  IMPACT_FACTS = 'ImpactFacts',
  PRODUCT_JOURNEY = 'ProductJourney',
  MORE_INFO = 'MoreInfoBlock',
  FEEDBACK = 'FeedbackBlock',
  MORE_PRODUCTS = 'ProductsBlock',
  VERIFIED_PAYMENTS = 'VerifiedPaymentsBlock',
}

export type TContentBlockType = `${EContentBlockType}`;

export interface IContentBlockDefaults {
  id: string;
  contentType: TContentBlockType;
  enabled: boolean;
  versionId: string;
}

export interface IAboutBlockData extends IContentBlockDefaults {
  contentType: EContentBlockType.ABOUT;
  data: {
    id: string;
    headline: string;
    description: string;
    image: ImageVariant;
  };
}

export interface ImpactFact {
  id: string;
  title: string;
  description: string;
  sdgs: number[];
}

export interface ImpactFactsData extends IContentBlockDefaults {
  contentType: EContentBlockType.IMPACT_FACTS;
  data: {
    id: string;
    headline: string;
    description: string;
    image: ImageVariant;
    impactFacts: ImpactFact[];
  };
}

export interface IMoreProductItem {
  id: string;
  title: string;
  description: string;
  image: ImageType;
  link?: string;
}

export interface IProductsBlockData extends IContentBlockDefaults {
  contentType: EContentBlockType.MORE_PRODUCTS;
  data: {
    id: string;
    moreProducts: IMoreProductItem[];
  };
}
export interface IProductsJourneyData extends IContentBlockDefaults {
  contentType: EContentBlockType.PRODUCT_JOURNEY;
  data: {
    id: string;
    headline: string;
    description: string;
    image: ImageVariant;
  };
}

export interface IMoreInfoData extends IContentBlockDefaults {
  contentType: EContentBlockType.MORE_INFO;
  data: {
    id: string;
    mediaItem: TMediaType;
    blockTitle: string;
    headline: string;
    description: string;
  };
}

export interface IFeedbackBlockData extends IContentBlockDefaults {
  contentType: EContentBlockType.FEEDBACK;
  data: {
    id: string;
    ratingEnabled: boolean;
    headline: string;
    subtitle: string;
  };
}
export interface IVerifiedPaymentsData extends IContentBlockDefaults {
  contentType: EContentBlockType.VERIFIED_PAYMENTS;
  data: {
    id: string;
    headline: string;
    text: string;
    image: ImageType;
  };
}

enum EProductStatus {
  'DRAFT' = 'DRAFT',
  'PUBLISHED' = 'PUBLISHED',
  'ARCHIVED' = 'ARCHIVED',
  'DELETED' = 'DELETED',
  'ERROR' = 'ERROR',
}

export interface IProductVersion {
  id: string;
  productId: string;
  title: string;
  progress: number;
  image: ImageVariant;
  published: boolean;
  publishedTimestamp?: string;
  firstPublishedTimestamp?: string;
  createdTimestamp: Date;
  lastEditedTimestamp?: string;
  status: EProductStatus;
  contentBlocks:
    | [
        IAboutBlockData,
        ImpactFactsData,
        IMoreInfoData,
        IProductsJourneyData,
        IFeedbackBlockData,
        IProductsBlockData
      ]
    | [
        IAboutBlockData,
        ImpactFactsData,
        IMoreInfoData,
        IProductsJourneyData,
        IFeedbackBlockData,
        IProductsBlockData,
        IVerifiedPaymentsData
      ];
  supplyChain?: IChainMapping;
  versionValidation: {
    valid: boolean;
    contentStatus: number;
  };
}
enum EProductAction {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  PUBLISHED = 'PUBLISHED',
  UNPUBLISHED = 'UNPUBLISHED',
  ARCHIVED = 'ARCHIVED',
  UNARCHIVED = 'UNARCHIVED',
  DELETED = 'DELETED',
}
interface IStyledProgress {
  status: 'PUBLISHED' | 'DRAFT' | 'ARCHIVED';
  color: string;
  contentStatus: number;
}

export interface IProduct {
  id: string;
  imageUrl: string;
  image: ImageVariant | null;
  publishedLanguages: ELanguage[];
  createdTimestamp: Date;
  modifiedTimestamp: Date;
  publicUrl: string;
  publishedTimestamp: Date;
  firstPublishedTimestamp: Date;
  slug: string;
  contentStatus: number;
  status: EProductStatus;
  title: string;
  defaultQrCode: IProductQrCodeDesign;
  languageConfig: {
    default: ELanguage;
    enabled: ELanguage[];
  };
  latestAction: {
    action: EProductAction;
    userName: string;
  };
  hasError?: boolean;
  // Type policies
  notEnabledLanguages: ELanguage[];
  styledProgress: IStyledProgress;
  isPublished?: boolean;
  articleNumber?: string;
  supplyChainId?: string;
  latestSupplyChain?: Pick<IBaseChain, 'id' | 'title' | 'chainTasksDetails'>;
  connectedPartnerCompanies?: IBasePartnerCompany[];
  __typename: 'Product';
}

export interface IProductQrCodeDesign {
  id: string;
  tag: string;
  url: string;
  design: {
    qrCodeLogo: number | null;
    qrCodePattern: number | null;
    qrCodeRotation: string | null;
    frameTemplate: string | null;
    backgroundColor: string | null;
    foregroundColor: string | null;
    markerBottomTemplate: string | null;
    markerBottomInnerColor: string | null;
    markerBottomOuterColor: string | null;
    markerLeftTemplate: string | null;
    markerLeftInnerColor: string | null;
    markerLeftOuterColor: string | null;
    markerRightTemplate: string | null;
    markerRightInnerColor: string | null;
    markerRightOuterColor: string | null;
  };
}
export interface ILanguageConfigOption {
  flag: string;
  key: ELanguage;
  label: string;
  title: string;
}
export interface ILanguageConfig {
  default: ELanguage;
  enabled: ELanguage[];
}
export enum ESupportedImageCustomFormats {
  PNG = 'PNG',
  JPEG = 'JPEG',
  SVG = 'SVG',
  EPS = 'EPS',
}

export enum ESupportedImageFromHTMLFormats {
  PNG = 'PNG',
  JPEG = 'JPEG',
  SVG = 'SVG',
  EPS = 'EPS',
}
export interface IUpdateContentBlocksOrderInput {
  contentBlocksOrder: IContentBlockOrderInput[];
}

export interface IContentBlockOrderInput {
  contentType: TContentBlockType;
}

export interface IUpdateContentBlocksOrderPayload {
  contentBlocksMeta: IContentBlockMeta[];
}

export interface IContentBlockMeta {
  id: string;
  productVersionId: string;
  contentType: TContentBlockType;
  order: number;
  enable: boolean;
}
